/**
 * dplabs.mjs — the DP101 labs' logic, PURE. What both benches need and neither authors.
 *
 * FOUNDER, 2026-08-20: "dp101 shall be a simulation, creation of dp ship a simulation, walk along
 * her a simulation and whatever parts and actions simulations will make it world class" — then
 * "start with ch2 and ch3".
 *
 * ── WHAT THIS FILE IS ALLOWED TO DO ───────────────────────────────────────────────────────────
 * Assemble a MODEL and ask dp-core about it. Nothing else. Every number a learner sees comes back
 * from `canHold(model, …)`, the same function the /dp page and the server engines run, and every
 * SENTENCE about the classes is quoted from the corpus entry that already carries its citation.
 *
 * This is the whole reason a bespoke teaching simulator was refused (vivechana 2026-08-20,
 * projection V=7560 F=9 vs bespoke V=360 F=3, the bespoke option marked IRREVERSIBLE): a thruster
 * response tuned to feel right is an invented fact wearing an animation, and a learner cannot
 * un-learn it. So there is no physics here. There is an assembly, a question, and a quote.
 *
 * ── WHY THE HULL IS FIXED AND ONLY THE DP SYSTEM VARIES ───────────────────────────────────────
 * Her wind and current areas are computed from her declared offsets. A learner-drawn hull would
 * have no declared offsets, so the forces would have to be invented — the one thing this may not
 * do. Keeping the hull and varying thrusters, generators, switchboards and the bus tie is also the
 * correct pedagogy for a chapter whose subject is that class is about REDUNDANCY, not hull.
 *
 * ── THE CLASS IS COMPUTED, NEVER ASSERTED ─────────────────────────────────────────────────────
 * `classOf()` runs the single-failure criterion over the assembled model. A learner who builds a
 * vessel that cannot survive a single active failure does not get "DP2" because he added a second
 * thruster; he gets the failure that broke it, named. That is FP-014 applied to teaching — the
 * notation is a compiled view of what was built, and a claim nobody can falsify is not a claim.
 */

/**
 * LOADING THE ENGINE, AND WHY THE SPECIFIER IS A VARIABLE.
 *
 * `/dp-core.mjs` is SERVED by slm-tutor at that path — it is not a source file the bundler can
 * reach, and it must not be bundled: the whole point is that the browser runs the same file the
 * server engines run, proven bit-identical by fingerprint. A literal `import('/dp-core.mjs')` makes
 * Rollup try to resolve it at build time and fail the build, and `@vite-ignore` alone does not stop
 * that for a static specifier. Holding the path in a constant makes the specifier dynamic, so it
 * survives to runtime as written.
 *
 * A bundled COPY would be the worse outcome, not the safer one: two copies of the physics that can
 * drift is precisely the fault the fingerprint check on /dp exists to prevent.
 */
export const DP_CORE_URL = '/dp-core.mjs';
export const loadCore = () => import(/* @vite-ignore */ DP_CORE_URL);

/* ── THE CLASS DEFINITIONS, QUOTED ─────────────────────────────────────────────────────────────
   Lifted verbatim from the corpus entries behind `dynamic-positioning`, each of which already
   carries its citation. Not paraphrased and not recalled: this file may not be the place a class
   definition is born. If the corpus is corrected, these must be re-lifted, not edited. */
export const CLASS_RULES = Object.freeze([
  {
    cls: 1,
    says: 'For equipment class 1, loss of position MAY occur in the event of a single fault.',
    cite: 'IMO MSC.1/Circ.1580 (2017), section 2 (equipment class 1 definition) and section 3 (functional requirements by class); identical class structure in IMO MSC/Circ.645 (1994), section 2.',
  },
  {
    cls: 2,
    says: 'For equipment class 2, loss of position is NOT to occur in the event of a single fault in any ACTIVE component or system.',
    cite: 'IMO MSC.1/Circ.1580 (2017), section 2 (equipment class 2 definition: single fault in any active component or system; static components normally excluded) and section 3 (redundancy requirements); IMO MSC/Circ.645 (1994), section 2.2.2 with the same active/static split.',
  },
  {
    cls: 3,
    /* THIS ONE WAS PARAPHRASED AND PRESENTED AS A QUOTE. My first version dropped "— the defining
       addition —" from the middle of the sentence, which reads as tidying and is not: the corpus is
       telling the learner WHICH half of the extension is the point of class 3, and I removed that
       signal while keeping the quotation marks. Caught by gate-dp101-labs comparing the text against
       the corpus, in the file whose whole discipline is that it may not be where a definition is
       born. Seven words, and the difference between a quote and a summary. */
    says: 'For equipment class 3, the single-failure criterion of class 2 is extended: the failure candidates additionally include all static components, and — the defining addition — ALL components in any one watertight compartment lost to flooding, and all components in any one fire subdivision lost to fire.',
    cite: 'IMO MSC.1/Circ.1580 (2017), section 2 (equipment class 3 definition) and section 3 (separation of redundant systems; backup control station in a separate compartment); IMO MSC/Circ.645 (1994), section 2.2.3.',
  },
]);

/* WHY CLASS 3 IS SHOWN BUT NEVER AWARDED. Its criterion is a COMPARTMENT lost to fire or flood, and
   the model this bench assembles carries no compartment boundaries — only switchboards. A bench
   that awarded class 3 would be reading a fact its data cannot hold, which is the fabrication this
   whole course is written against. So the bench computes 1 and 2, states that 3 is out of its
   reach, and says why. Refusing to answer is an answer; guessing is not. */
export const CLASS3_REFUSAL =
  'This bench cannot award class 3. Its criterion is every component in one watertight compartment '
  + 'lost to flooding, or one fire subdivision lost to fire — and the model you are assembling '
  + 'declares switchboards, not compartment boundaries. Awarding it would mean reading a fact the '
  + 'data does not hold.';

/* ── THE ENVIRONMENT IS HERS, NOT MINE ─────────────────────────────────────────────────────────
   The first draft of this bench invented one: 30 kn and 1.5 kn, chosen because they sounded like a
   working day. She DECLARES a station-keeping criterion — 35 kn, 2.0 kn of current, Hs 2.5 m, all
   acting 90° to the fore-and-aft axis — and her declaration says in as many words that it is "the
   criterion a real 79 m DP2 PSV specification of this class is written to… not an ANKR invention".
   Authoring a weather when the vessel declares one is the same fault as authoring a fact.
   Held fixed rather than put on a slider: if a learner could turn the weather down, he could earn a
   class notation by calming the sea instead of by building redundancy. */
export const criterionOf = (model) => model.station_keeping;
export const benchEnv = (core, model, from_deg = null) => {
  const c = criterionOf(model);
  const a = from_deg === null ? 90 : from_deg;
  return { wind_kn: c.wind_kn, wind_from_deg: a, current_kn: c.current_kn, current_from_deg: a,
    Hs_m: c.wind_kn * core.COEFFICIENTS.sea_state.Hs_per_kn, h_over_T: 99 };
};
export const benchEnvWhy = (model) => {
  const c = criterionOf(model);
  return `Her declared station-keeping criterion: ${c.wind_kn} kn of wind, ${c.current_kn} kn of current and `
    + `Hs ${c.Hs_m} m, ${c.direction}. Not a condition this bench invented — ${c.basis}`;
};

/* THE LIGHT ENVIRONMENT the control question is asked in — 5 kn and 0.5 kn, the same pair the
   server's class-2 verdict uses, so the bench and the chapter cannot disagree about what "retains
   control" means. */
export const LIGHT_ENV = Object.freeze({ wind_kn: 5, current_kn: 0.5 });
export const lightEnv = (core, from_deg) => ({
  wind_kn: LIGHT_ENV.wind_kn, wind_from_deg: from_deg, current_kn: LIGHT_ENV.current_kn,
  current_from_deg: from_deg, Hs_m: LIGHT_ENV.wind_kn * core.COEFFICIENTS.sea_state.Hs_per_kn, h_over_T: 99,
});

/**
 * THE SEA IS RAISED FROM THE WIND, AND THIS IS WHY THE BENCH SAYS SO OUT LOUD.
 *
 * ── TWO BENCHES IN ONE COURSE GAVE DIFFERENT ANSWERS FOR THE SAME SHIP ────────────────────────
 * The server's capability engine derives significant wave height from wind speed —
 * `Hs = wind_kn × Hs_per_kn` — and chapter 6 publishes its output: after the worst-case failure
 * STHIR holds 71.2 kn head to weather and 0 kn at 60°. The first version of this bench passed
 * `Hs_m: null`, which dp-core reads as no sea at all, so it computed 80.5 kn and 31.5 kn for the
 * same vessel and the same failure. Both were internally consistent; they were answering different
 * questions, and nothing on either screen said so.
 *
 * A learner meeting 32.5 kn on the failure walk and 0 kn on the polar has no way to reconcile them,
 * and the likeliest thing he concludes is that one of them is wrong. In a course whose discipline is
 * that every number carries its provenance, shipping two unreconciled numbers would be worse than
 * shipping neither.
 *
 * So the sea is raised the same way here. One method, one answer, no reconciliation asked of the
 * reader. The relation is a declared plotting convenience rather than a sea-state model — dp-core
 * says so in its own coefficient — and that limitation belongs on the screen with the number.
 */
export const HS_FROM_WIND_NOTE =
  'Significant wave height is raised from the wind by the engine\'s declared relation (Hs 2.5 m at '
  + '35 kn, scaled linearly). That is a plotting convenience, not a sea-state model — a real sea '
  + 'depends on fetch and duration — and it is the same relation the capability plot in chapter 6 '
  + 'uses, so the two agree.';

/**
 * The bench environment at a given wind, with the sea raised from it exactly as the server does.
 *
 * THE COEFFICIENT IS READ OFF THE ENGINE, never written here. The first draft carried
 * `0.07142857142857142` as a default parameter, which is a copy of a declared constant sitting one
 * file away from the declaration — the hand-kept-map fault, in its smallest and most tempting form.
 * If dp-core ever revises the relation, a copy here would go on plotting the old sea in silence
 * while the chapter-6 polar moved. So `core` is a required argument and the number has one home.
 */
export const envAt = (core, model, wind_kn, from_deg = 90) => ({
  ...benchEnv(core, model, from_deg), wind_kn, Hs_m: wind_kn * core.COEFFICIENTS.sea_state.Hs_per_kn,
});

/** The parts bin — HER declared units and nothing invented. Filled from /api/dp/model. */
export function partsFrom(model) {
  if (!model) return { thrusters: [], generators: [], switchboards: [] };
  return {
    thrusters: model.thrusters || [],
    generators: model.generators || [],
    switchboards: model.switchboards || [],
  };
}

/** A model carrying only the units the learner has switched on. Same shape dp-core already takes. */
export function assemble(model, picked) {
  const on = (id) => picked.includes(id);
  return {
    ...model,
    thrusters: (model.thrusters || []).filter((t) => on(t.id)),
    generators: (model.generators || []).filter((g) => on(g.id)),
  };
}

/**
 * THE SINGLE-FAILURE CANDIDATES — the ACTIVE components, because that word is the class-2 rule.
 *
 * The quoted definition names "generators, thrusters, switchboards, remote-controlled valves and
 * similar — things that do something and can therefore fail while doing it". This enumerates the
 * three of those four the model declares. It does not enumerate cables or manual valves, and the
 * reason is the rule itself rather than convenience: static components are normally excluded at
 * class 2, and including them here would be inventing a stricter standard than the one cited.
 */
export function activeFailures(built) {
  const out = [];
  for (const t of built.thrusters || []) out.push({ id: `thruster:${t.id}`, kind: 'thruster', label: `${t.id} (${t.type}, ${t.kw} kW) fails`, dead: [t.id], deadGenerators: [] });
  for (const g of built.generators || []) out.push({ id: `generator:${g.id}`, kind: 'generator', label: `${g.id} (${g.kw} kW) fails`, dead: [], deadGenerators: [g.id] });
  /* A SWITCHBOARD IS A STRING, NOT AN OBJECT — `switchboards: ["PORT","STBD"]`, and the units point
     back at it by name. The first version read `b.id`, which is undefined on a string, so every
     board matched nothing and the whole switchboard family vanished from the candidate list in
     silence. That is the failure the chapter calls the normal worst case, absent, with no error.
     Guessing the shape of the field you are iterating is the same fault as guessing its contents. */
  for (const b of built.switchboards || []) {
    const board = typeof b === 'string' ? b : b.id;
    const dead = (built.thrusters || []).filter((t) => t.switchboard === board).map((t) => t.id);
    const deadGenerators = (built.generators || []).filter((g) => g.switchboard === board).map((g) => g.id);
    if (!dead.length && !deadGenerators.length) continue;   // a board carrying nothing is not a failure
    out.push({ id: `switchboard:${board}`, kind: 'switchboard', label: `${board} switchboard lost, with everything on it`, dead, deadGenerators });
  }
  return out;
}

/**
 * WHICH FAILURE TAKES THE MOST — the worst case, COMPUTED over the candidates.
 *
 * Chapter 3 says the worst-case failure "is the single failure that takes the most capability with
 * it", and that on a two-split diesel-electric vessel it is normally the loss of one complete
 * switchboard with its generators and its thrusters. NORMALLY is doing work in that sentence, so
 * this computes it rather than assuming it: the bench must be able to show a build where the worst
 * case is something else, or it is teaching the usual answer as if it were the rule.
 */
/**
 * HOW MUCH WEATHER SHE STILL HOLDS AFTER A FAILURE — the measure "takes the most capability" needs.
 *
 * ── THE FIRST VERSION OF THIS WAS A FABRICATION AND THE BENCH ALMOST SHIPPED IT ────────────────
 * It read `allocation.utilisation` and called `1 - utilisation` the margin. dp-core's allocation
 * returns `{feasible, units, demand, board_power, binding}` — there is no `utilisation`. So every
 * margin evaluated to 0, every candidate tied, and `sort` returned the FIRST one. The bench would
 * have named "AZ-P fails" as the computed worst case when it was array order, in a chapter whose
 * whole subject is that the worst case is a specific computed sentence. A number that is quietly
 * `undefined` reads exactly like a number that was measured.
 *
 * So the capability is measured the way the definition states it: step the wind up until she can no
 * longer hold, and report the last speed at which she did. The failure that leaves the LOWEST limit
 * is the one that took the most. Current is held at the bench value and only wind is stepped — the
 * same single-variable sweep the capability plot in chapter 6 uses, and stated here rather than
 * assumed, because a limit is meaningless without saying what was varied to find it.
 */
/* ── HOW THE LIMIT IS FOUND: BINARY SEARCH, BECAUSE THAT IS WHAT THE SERVER DOES ───────────────
   A fixed step was the first version and it was honest but coarse — 2 kn steps put her at 70 kn
   head to weather where the chapter publishes 71.2, and a learner reading both would think one of
   them wrong. The server's capability engine brackets and halves fourteen times; doing anything
   else here would be a second method quietly producing a second answer for the same vessel.
   Same method, same number, nothing for the reader to reconcile. */
export const WIND_CEILING_KN = 120;
export const SEARCH_ITERATIONS = 14;

export function windLimit(built, core, model, dead = [], deadGenerators = [], from_deg = 90) {
  const holds = (w) => core.canHold(built, envAt(core, model, w, from_deg), dead, deadGenerators).feasible === true;
  /* She may already be unable to hold in still air — a real answer, and it must stay
     distinguishable from "the search never ran". */
  if (!holds(0)) return null;
  if (holds(WIND_CEILING_KN)) return WIND_CEILING_KN;   // capped, not extrapolated beyond the ceiling
  let lo = 0, hi = WIND_CEILING_KN;
  for (let i = 0; i < SEARCH_ITERATIONS; i++) {
    const mid = (lo + hi) / 2;
    if (holds(mid)) lo = mid; else hi = mid;
  }
  return Math.round(lo * 10) / 10;
}

export const HEADING_STEP_DEG = 15;

/**
 * THE THREE QUESTIONS, KEPT APART — chapter 6 §6, and the transferable part of the whole course.
 *
 *   1. Does she keep CONTROL?          the class 2 question
 *   2. What ENVELOPE remains?          the capability question
 *   3. On SOME heading, or EVERY one?  the operating question
 *
 * ── I COLLAPSED THEM, EXACTLY AS THE CHAPTER SAYS THE ENGINE ONCE DID ─────────────────────────
 * My first `classOf` awarded class 2 only if she still held the full criterion after every single
 * failure. That is the wrong test, and the chapter is written about the day this project made it:
 * "The first verdict this engine produced said FAIL… The test was the wrong test." Post-failure
 * envelopes are smaller on every DP2 ship afloat — if they were not, capability plots would not
 * publish in pairs. Testing question 3 and calling it question 1 fails a compliant vessel.
 *
 * The chapter also names why a false negative is the dangerous direction rather than the safe one:
 * "a check that fails safely-but-wrongly gets overridden, and an overridden check protects nothing
 * afterwards." So the repair is not a kinder threshold, it is three separate answers.
 */
export function threeQuestions(built, core, model, dead, deadGenerators) {
  const crit = criterionOf(model);
  const pts = [];
  for (let a = 0; a < 360; a += HEADING_STEP_DEG) {
    pts.push({ alpha_deg: a, max_wind_kn: windLimit(built, core, model, dead, deadGenerators, a) });
  }
  const val = (p) => (p.max_wind_kn === null ? 0 : p.max_wind_kn);
  const best = pts.reduce((m, p) => (val(p) > val(m) ? p : m), pts[0]);
  const worstPt = pts.reduce((m, p) => (val(p) < val(m) ? p : m), pts[0]);
  /* Question 1 is asked in the LIGHT environment at her best heading — the server's own test, so
     the bench cannot mean something different by "retains control" than the chapter does. */
  const light = core.canHold(built, lightEnv(core, best.alpha_deg), dead, deadGenerators);
  return {
    points: pts,
    retains_control: light.feasible === true,
    best_heading_deg: best.alpha_deg, best_max_wind_kn: val(best),
    worst_heading_deg: worstPt.alpha_deg, worst_max_wind_kn: val(worstPt),
    holds_criterion_any_heading: val(best) >= crit.wind_kn,
    holds_criterion_every_heading: val(worstPt) >= crit.wind_kn,
    criterion_kn: crit.wind_kn,
  };
}

export function worstCase(built, core, model) {
  const cands = activeFailures(built);
  const rows = cands.map((c) => {
    const r = core.canHold(built, benchEnv(core, model), c.dead, c.deadGenerators);
    const limit = windLimit(built, core, model, c.dead, c.deadGenerators);
    return { ...c, feasible: !!r.feasible, limit_kn: limit, binding: r.allocation && r.allocation.binding };
  });
  const broken = rows.filter((r) => !r.feasible);
  /* Worst = the failure that takes the most capability. Among those that break her outright, and
     otherwise across all of them — a vessel that survives everything still has a failure that costs
     the most, and that sentence is what her ASOG is written against. */
  const pool = broken.length ? broken : rows;
  const rank = (r) => (r.limit_kn === null ? -1 : r.limit_kn);
  const sorted = pool.slice().sort((a, b) => rank(a) - rank(b));
  const worst = sorted[0] || null;
  /* A TIE IS A FACT, NOT A PICK. Two failures can genuinely cost the same — a symmetric two-split
     vessel is the ordinary case, not the exception. Saying "the worst case is X" when Y is equal
     would be the array-order fabrication again, wearing a computation. */
  const tied = worst ? sorted.filter((r) => rank(r) === rank(worst)) : [];
  return { rows, worst, tied, survivesAll: broken.length === 0 };
}

/**
 * THE NOTATION, COMPUTED FROM WHAT WAS BUILT.
 *
 * class 2 → she holds intact AND after every single active failure
 * class 1 → she holds intact, and some single failure loses position
 * none    → she cannot hold intact at all, which no class permits
 *
 * Class 3 is never returned; see CLASS3_REFUSAL for the reason it is refused rather than guessed.
 */
export function classOf(built, core, model) {
  const intact = core.canHold(built, benchEnv(core, model));
  if (!intact.feasible) {
    return { cls: 0, intact: false, rows: [], worst: null, tied: [],
      why: 'She cannot hold her own declared criterion intact, before any failure is considered. No equipment class describes a vessel that cannot hold station at all.' };
  }
  const { rows, worst, tied } = worstCase(built, core, model);
  /* THE CLASS 2 TEST IS QUESTION 1, ASKED OF EVERY SINGLE FAILURE — does she keep CONTROL. Not
     whether the intact envelope survives; that is question 3, and conflating them is the recorded
     mistake this bench nearly repeated. */
  const q = activeFailures(built).map((c) => ({ c, q: threeQuestions(built, core, model, c.dead, c.deadGenerators) }));
  const lostControl = q.filter((x) => !x.q.retains_control);
  if (!lostControl.length) {
    return { cls: 2, intact: true, rows, worst, tied, questions: q,
      why: 'She holds intact, and every single active failure still leaves her able to hold a light environment on some heading — she keeps CONTROL. That is the class 2 criterion. Her envelope after a failure is smaller, on every DP2 ship afloat; that is an operating limit, not a failure of class.' };
  }
  return { cls: 1, intact: true, rows, worst, tied, questions: q,
    why: `She holds intact, but ${lostControl.length === 1 ? 'one single failure costs her control' : `${lostControl.length} single failures cost her control`} — she cannot hold even a light environment afterwards. A single fault in an active component may cause loss of position; that is class 1.` };
}

/** The quoted rule for a computed result, so the verdict and its authority travel together. */
export const ruleFor = (cls) => CLASS_RULES.find((r) => r.cls === cls) || null;

export const WHY_NOT_SCORED =
  'Nothing here is scored. The skill is not assembling the cheapest vessel that passes — it is '
  + 'seeing WHICH failure decides the class, because that sentence is the worst-case failure design '
  + 'intent and everything in DP operations keys off it.';

export const SOURCE_CH2 = 'DP101 chapter 2 — The Classes Are About Redundancy, Not Hull';
export const SOURCE_CH3 = 'DP101 chapter 3 — The Worst-Case Failure, and Designing For It';

/* ══ CHAPTER 8 — ASOG, THE WATCH CIRCLE, AND LOSING POSITION ══════════════════════════════════
 *
 * This chapter teaches DECISIONS, not forces, so its bench is a classification rather than a
 * simulation — and the classification is a real one: the two loss-of-position mechanisms have
 * OPPOSITE first actions, which is exactly the kind of pair that is learned wrongly and then acted
 * on wrongly under pressure.
 *
 * EVERY SENTENCE BELOW IS THE CHAPTER'S. Not summarised: a summary of an emergency action is how a
 * first action gets softened. gate-dp101-labs greps each `says` against 08-operating-her.md, so a
 * paraphrase here fails the build exactly as the class-3 paraphrase did.
 */
export const ASOG_STATUSES = Object.freeze([
  { id: 'green', says: 'normal operation' },
  { id: 'advisory', says: 'something has degraded; watch it' },
  { id: 'degraded', says: 'make ready to suspend; move to a safe standoff' },
  { id: 'emergency', says: 'terminate the work and clear the asset' },
]);

export const MECHANISMS = Object.freeze([
  {
    id: 'drive-off',
    name: 'DRIVE-OFF',
    powered: true,
    says: 'A DRIVE-OFF is powered.',
    how: 'The system — misled by a faulted reference or sensor, or commanded wrongly — THRUSTS the vessel away from the setpoint',
    danger: 'it is dangerous alongside structures because the vessel arrives with way on',
    first_action: 'take manual control and stop the powered push before it builds way.',
    why: 'The system is the problem; removing the system from the loop is the fix. Every second of powered movement is momentum that must later be arrested.',
  },
  {
    id: 'drift-off',
    name: 'DRIFT-OFF',
    powered: false,
    says: 'A DRIFT-OFF is unpowered.',
    how: 'Thrust is lost or insufficient — blackout, failure beyond the design intent, or weather beyond capability — and the environment carries her off',
    danger: 'accelerating as the beam comes to the weather',
    first_action: 'bring the head to the weather',
    why: 'the heading with the smallest windage and the fullest remaining capability',
  },
]);

/* The three triggers, each with the chapter it arrived from. "Each maps to a written action. None
   of them is a judgement call at the time. That is the point." */
export const TRIGGERS = Object.freeze([
  { id: 'ca-alarm', says: 'a consequence-analysis alarm', from: 'chapter 6', means: 'the insurance has lapsed' },
  { id: 'references', says: 'references below the required count', from: 'chapter 5', means: 'she is losing her knowledge of where she is' },
  { id: 'yellow', says: 'a yellow-limit excursion', from: 'the watch circle', means: 'she has moved further than the work tolerates' },
]);

/**
 * THE SCENARIOS. Each is a re-statement of a mechanism the chapter DEFINES, not an incident I
 * invented — an invented casualty is written to be recognisable, which teaches recognising my
 * writing rather than the mechanism. The discriminator in every one is the same single question the
 * chapter puts at the centre: is thrust being APPLIED, or is it ABSENT?
 */
export const SCENARIOS = Object.freeze([
  { id: 's1', text: 'A position reference has gone faulty and is feeding the system a position that is slowly walking away from the truth. The thrusters are answering it at full power.', answer: 'drive-off',
    because: 'The system is misled and is THRUSTING her away from the setpoint. Power is being applied, so she will arrive with way on.' },
  { id: 's2', text: 'A blackout. She has lost her generation and the thrusters are dead. The weather is on the beam and she is beginning to move downwind.', answer: 'drift-off',
    because: 'Thrust is absent and the environment is carrying her. It begins slowly and accelerates — fastest once the beam is to the weather, which is where post-failure capability is least.' },
  { id: 's3', text: 'A failure beyond the design intent has left her with far less thrust than the weather demands. Everything still running is at its limit and she is losing ground.', answer: 'drift-off',
    because: 'Thrust is INSUFFICIENT rather than misdirected. The chapter puts "weather beyond capability" and "failure beyond the design intent" in the same mechanism as blackout.' },
  { id: 's4', text: 'A wrong command has been accepted and the vessel is being driven off the setpoint under full control of the DP system. Nothing has failed.', answer: 'drive-off',
    because: 'Commanded wrongly is named in the definition alongside a faulted reference. Nothing has to have BROKEN for a drive-off.' },
]);

export const NEAR_LOSS = 'A LARGE EXCURSION short of either is logged and investigated as a near-loss.';
export const HEADING_IS_THE_VARIABLE = 'Heading is not a comfort. It is the variable that decides how much capability she has left.';
export const ESCAPE_ROUTE = 'along the pre-agreed escape route';
export const SOURCE_CH8 = 'DP101 chapter 8 — Operating Her: ASOG, the Watch Circle, and Losing Position';

/* ══ CHAPTERS 1, 4, 5 AND 7 ═══════════════════════════════════════════════════════════════════
 *
 * Same discipline as chapter 8's block above: every `says` is greped against its chapter file by
 * gate-dp101-labs, with markdown emphasis stripped and nothing else normalised. Four fragments
 * failed that check on the first run and every one was MY typography — curly apostrophes and
 * quotes where the chapters use straight ones. Trivial, and exactly the kind of drift that makes a
 * quotation not a quotation.
 */

/* ── CHAPTER 1 · what a DP claim must NAME to be a claim at all ─────────────────────────────── */
export const SOURCE_CH1 = 'DP101 chapter 1 — What Dynamic Positioning Is, and What It Replaces';
export const DP_DEFINITION = 'exclusively by means of thruster force';
export const BEAM_IS_THE_SPEC = 'A vessel that meets her criterion on the beam meets it more easily on any other heading, so the beam case IS the specification.';
export const CLAIM_SHAPE = 'It names an environment, a heading and a power state, and stops.';
export const CLAIM_PARTS = Object.freeze([
  { id: 'environment', name: 'an environment', how: 'wind, sea and current, with figures' },
  { id: 'heading', name: 'a heading', how: 'which way the elements act on her' },
  { id: 'power', name: 'a power state', how: 'what the plant is doing while she holds it' },
]);
/* The incomplete claims are the ones this industry actually makes. A claim invented to be obviously
   empty teaches spotting my writing; "DP2, holds position" is on real brochures. */
export const CLAIMS_CH1 = Object.freeze([
  { id: 'c1', text: 'DP2, holds position.', has: [] },
  { id: 'c2', text: 'Maintains station in 35 knots of wind.', has: ['environment'] },
  { id: 'c3', text: 'Maintains station in 35 kn wind, sea state 4 and 2.0 kn current, all elements acting at 90 degrees to the fore-and-aft axis.', has: ['environment', 'heading'] },
  { id: 'c4', text: 'Capable of operating 24 hours a day at 100 % MCR while maintaining station in Beaufort force 7 up to 35 knots, sea state 4, and a current of 2.0 knots, with all elements acting at 90 degrees to the fore-and-aft axis.', has: ['environment', 'heading', 'power'] },
]);

/* ── CHAPTER 4 · forbidden arcs, and three numbers that are not one number ──────────────────── */
export const SOURCE_CH4 = 'DP101 chapter 4 — Thrusters, and Who Decides What Each One Does';
export const FORBIDDEN_IS = 'A forbidden zone is a sector where a unit may not point, because its wash would strike another thruster or the hull — robbing both of thrust.';
export const INTERACTION_IS = "Thruster–thruster interaction is why two 2,000 kW units do not give twice one unit's holding force in every direction.";
export const RATINGS_ARE = "Ratings are the maker's.";
export const CLAIMABLE_IS = "The delivered thrust a DP calculation may claim comes from the vessel's own documentation or model tests, not from a catalogue figure alone.";
export const THREE_NUMBERS = 'Not one number pretending to be all three.';

/* ── CHAPTER 5 · why three references and not two ──────────────────────────────────────────── */
export const SOURCE_CH5 = 'DP101 chapter 5 — Knowing Where She Is: References and Sensors';
export const VOTING_IS = 'The control system weights and votes the references, rejecting one that diverges.';
export const TWO_IS_NOT_ENOUGH = 'With two references and a disagreement, there is no majority and no way to know which is lying.';
export const DPO_WATCHES = 'A DPO watches reference health continuously.';

/* ── CHAPTER 7 · the document that turns a claim into an audited property ───────────────────── */
export const SOURCE_CH7 = 'DP101 chapter 7 — Proving the Claim: FMEA and DP Trials';
export const FMEA_ENUMERATES = 'enumerates the failure modes of every element of the DP system';
export const FMEA_ELEMENTS = Object.freeze(['generation', 'distribution', 'thrusters', 'control', 'references', 'sensors']);
export const TRIALS_INJECT = 'verified physically by FMEA PROVING TRIALS';
export const TRIALS_ACTS = Object.freeze(['breakers opened', 'signals failed', 'a switchboard blacked out']);
export const DP2_WITHOUT_IT = 'A generic claim of "DP2" with an unproven or stale FMEA is a hull with two switchboards, not an equipment class.';
